import * as fs from "node:fs";
import * as path from "node:path";
import * as os from "node:os";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";
import { LocalCliError } from "./errors.js";

export interface BrainBlock {
  repo: string;
  branch: string;
  installationId: string;
  credentialRef: string;
  linkedAt: string; // ISO 8601 UTC
}

/**
 * On-disk shape of ~/.m8t/foundry/<agent>.yaml.
 *
 * The base fields (agentName, agentId, ..., environments) are always present.
 * Two optional blocks:
 *   - fillableFieldValues: populated by the architect at deploy time;
 *     used by brain-link/brain-unlink to re-render the persona body
 *     deterministically without re-prompting the operator.
 *   - brain: populated only by `m8t brain link` / `create`; absent for
 *     non-brain workers.
 */
export interface AgentYaml {
  agentName: string;
  agentId: string;
  projectEndpoint: string;
  model: string;
  personaVersion: string | null;
  personaPath: string;
  deployedAt: string;
  playgroundUrl: string;
  environments: { default: { projectEndpoint: string } };
  fillableFieldValues?: Record<string, string>;
  brain?: BrainBlock;
}

export function agentYamlPath(agentName: string, home: string = os.homedir()): string {
  return path.join(home, ".m8t", "foundry", `${agentName}.yaml`);
}

export function readAgentYaml(agentName: string, home?: string): AgentYaml | null {
  const file = agentYamlPath(agentName, home);
  if (!fs.existsSync(file)) return null;
  const text = fs.readFileSync(file, "utf8");
  return parseYaml(text) as AgentYaml;
}

export function writeAgentYaml(
  agentName: string,
  data: AgentYaml,
  home?: string,
): void {
  const file = agentYamlPath(agentName, home);
  fs.mkdirSync(path.dirname(file), { recursive: true });
  fs.writeFileSync(file, stringifyYaml(data), { mode: 0o600 });
}

/**
 * Patch fields in-place. `brain: null` removes the brain block (different
 * from `brain: undefined` which leaves it alone). All other null/undefined
 * fields are left alone — only explicit values overwrite.
 */
export function patchAgentYaml(
  agentName: string,
  patch: Omit<Partial<AgentYaml>, 'brain'> & { brain?: BrainBlock | null },
  home?: string,
): void {
  const current = readAgentYaml(agentName, home);
  if (!current) {
    throw new LocalCliError({
      code: "AGENT_YAML_NOT_FOUND",
      message: `Per-agent metadata file not found for '${agentName}'.`,
      hint: `Expected at ${agentYamlPath(agentName, home)}. Re-deploy the agent via the architect to recreate it.`,
    });
  }
  // Rebuild from known fields explicitly so unknown keys on disk (e.g.
  // deprecated fields from older versions) are dropped on the next patch —
  // not silently preserved by a spread.
  const next: AgentYaml = {
    agentName: current.agentName,
    agentId: current.agentId,
    projectEndpoint: current.projectEndpoint,
    model: current.model,
    personaVersion: current.personaVersion,
    personaPath: current.personaPath,
    deployedAt: current.deployedAt,
    playgroundUrl: current.playgroundUrl,
    environments: current.environments,
    ...(current.fillableFieldValues !== undefined && { fillableFieldValues: current.fillableFieldValues }),
    ...(current.brain !== undefined && { brain: current.brain }),
  };

  // Apply patch — brain has special null-clears-the-block semantics; all
  // other fields overwrite when defined.
  for (const [k, v] of Object.entries(patch)) {
    if (k === "brain") {
      if (v === null) {
        delete next.brain;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Object.entries strips optional marker; v can be undefined at runtime
      } else if (v !== undefined) {
        next.brain = v as BrainBlock;
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Object.entries strips optional marker; v can be undefined at runtime
    } else if (v !== undefined) {
      (next as unknown as Record<string, unknown>)[k] = v;
    }
  }
  writeAgentYaml(agentName, next, home);
}
